import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { ReactComponent as SaveIcon } from "../assets/svg/components/save.svg";
import { ReactComponent as CancelIcon } from "../assets/svg/view/close_circle.svg";
import PersonalInfoEdit from "./profile/PersonalInfoEdit";
import ContactInfoEdit from "./profile/ContactInfoEdit";
import StreamingMedia from "../utils/StreamingMedia";
import "../assets/scss/views/UserProfileEdit.scss";

const UserProfileEdit = ({
  updateData,
  onUpdateChange,
  onSubmit,
  onCancel,
  profileImageId,
  isUpdating,
  theme,
}) => {
  // Preparar datos para el título
  const getUserDisplayName = useMemo(() => {
    const { firstName, firstLastName, secondLastName } = updateData.personal;
    return `${firstName || ""} ${firstLastName || ""} ${secondLastName || ""}`.trim() || "Usuario";
  }, [updateData.personal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  // Validar si hay cambios pendientes de guardar
const hasUnsavedChanges = useMemo(() => {
  const personalHasChanges = Object.values(updateData.personal).some(value => 
    value?.toString().trim()
  );
  const contactHasChanges = Object.values(updateData.contact).some(value => 
    value?.toString().trim()
  );
  const systemHasChanges = Object.entries(updateData.system).some(([key, value]) => {
    // Para campos booleanos, verificar que no sean valores por defecto
    if (typeof value === 'boolean') {
      if (key === 'activeStatus' || key === 'limitedNotifications' || key === 'ownFleet') {
        return false;
      }
    }
    return value?.toString().trim();
  });
  
  return personalHasChanges || contactHasChanges || systemHasChanges;
}, [updateData]);

  return (
    <div className={`GM__${theme}-user-profile-edit`}>
      <form onSubmit={handleSubmit} className={`GM__${theme}-edit-form`}>
        {/* Botones de acción */}
        <div className={`GM__${theme}-action-buttons`}>
          <button 
            type="submit" 
            className={`GM__${theme}-save-button`}
            title="Guardar cambios"
            disabled={isUpdating}
          >
            <SaveIcon />
            {isUpdating ? "Guardando..." : "Guardar"}
          </button>
          <button 
            type="button" 
            onClick={onCancel}
            className={`GM__${theme}-cancel-button`}
            title="Cancelar edición"
            disabled={isUpdating}
          >
            <CancelIcon />
            Cancelar
          </button>
        </div>

        <div className={`GM__${theme}-edit-container`}>
          {/* Header con imagen y campos principales */}
          <div className={`GM__${theme}-edit-header`}>
            <div className={`GM__${theme}-user-image-container`}>
              <div className={`GM__${theme}-image-circle`}>
                <StreamingMedia
                  fileId={profileImageId}
                  className={`GM__${theme}-user-image`}
                />
              </div>
            </div>
            
            <div className={`GM__${theme}-header-content`}>
              <h2 className={`GM__${theme}-user-title`}>
                {getUserDisplayName}
              </h2>
              <div className={`GM__${theme}-title-underline`}></div>
              <div className={`GM__${theme}-header-info`}>
                <div className={`GM__${theme}-edit-status`}>
                  <span className={`GM__${theme}-edit-status-text`}>
                    {hasUnsavedChanges ? "⚠️ Cambios sin guardar" : "✓ Sin cambios pendientes"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Formulario organizado en secciones */}
          <div className={`GM__${theme}-form-sections`}>
            {/* Sección de Información Personal */}
            <div className={`GM__${theme}-form-section`}>
              <div className={`GM__${theme}-section-header`}>
                <h3 className={`GM__${theme}-section-title`}>
                  Información Personal
                </h3>
                <p className={`GM__${theme}-section-description`}>
                  Datos de identificación y información básica del usuario
                </p>
              </div>
              <div className={`GM__${theme}-section-content`}>
                <PersonalInfoEdit
                  personalInfo={updateData.personal}
                  systemInfo={updateData.system}
                  onPersonalInfoChange={(field, value) =>
                    onUpdateChange("personal", field, value)
                  }
                  onSystemInfoChange={(field, value) =>
                    onUpdateChange("system", field, value)
                  }
                  theme={theme}
                />
              </div>
            </div>

            {/* Sección de Información de Contacto */}
            <div className={`GM__${theme}-form-section`}>
              <div className={`GM__${theme}-section-header`}>
                <h3 className={`GM__${theme}-section-title`}>
                  Información de Contacto
                </h3>
                <p className={`GM__${theme}-section-description`}>
                  Datos de comunicación y ubicación del usuario
                </p>
              </div>
              <div className={`GM__${theme}-section-content`}>
                <ContactInfoEdit
  contactInfo={updateData.contact}
  systemInfo={updateData.system} // Nueva prop para EPS y ARL
  onContactInfoChange={(field, value) =>
    onUpdateChange("contact", field, value)
  }
  onSystemInfoChange={(field, value) => // Nueva prop
    onUpdateChange("system", field, value)
  }
  theme={theme}
/>

              </div>
            </div>

            {/* Sección de Configuraciones Adicionales */}
            <div className={`GM__${theme}-form-section`}>
              <div className={`GM__${theme}-section-header`}>
                <h3 className={`GM__${theme}-section-title`}>
                  Configuraciones del Perfil
                </h3>
                <p className={`GM__${theme}-section-description`}>
                  Ajustes adicionales y preferencias del usuario
                </p>
              </div>
              <div className={`GM__${theme}-section-content`}>
                <div className={`GM__${theme}-config-grid`}>
                  <div className={`GM__${theme}-config-item`}>
                    <div className={`GM__${theme}-config-info`}>
                      <span className={`GM__${theme}-config-label`}>
                        Estado del email:
                      </span>
                      <span className={`GM__${theme}-config-value readonly`}>
                        Pendiente de verificación
                      </span>
                    </div>
                    <div className={`GM__${theme}-config-description`}>
                      <span className={`GM__${theme}-config-hint`}>
                        El email será verificado automáticamente tras guardar cambios
                      </span>
                    </div>
                  </div>

                  <div className={`GM__${theme}-config-item`}>
                    <div className={`GM__${theme}-config-info`}>
                      <span className={`GM__${theme}-config-label`}>
                        Última actualización:
                      </span>
                      <span className={`GM__${theme}-config-value readonly`}>
                        {new Date().toLocaleDateString("es-CO")}
                      </span>
                    </div>
                    <div className={`GM__${theme}-config-description`}>
                      <span className={`GM__${theme}-config-hint`}>
                        Los cambios se reflejarán inmediatamente en el sistema
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Botones de acción del formulario (para móvil) */}
            <div className={`GM__${theme}-form-actions-mobile`}>
              <button
                type="submit"
                className={`GM__${theme}-save-button-mobile`}
                disabled={isUpdating}
              >
                <SaveIcon />
                {isUpdating ? "Guardando..." : "Guardar Cambios"}
              </button>
              <button
                type="button"
                className={`GM__${theme}-cancel-button-mobile`}
                onClick={onCancel}
                disabled={isUpdating}
              >
                <CancelIcon />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

UserProfileEdit.propTypes = {
  updateData: PropTypes.shape({
    personal: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      firstLastName: PropTypes.string.isRequired,
      secondLastName: PropTypes.string,
      documentNumber: PropTypes.string,
      documentTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      documentExpDate: PropTypes.string,
      documentExpeditionPlace: PropTypes.string,
      nationality_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      birthDate: PropTypes.string,
      department: PropTypes.string,
      bloodType: PropTypes.string,
      jobPosition: PropTypes.string,
      customJobPosition: PropTypes.string,
    }).isRequired,
    contact: PropTypes.shape({
      primaryPhoneNumber: PropTypes.string,
      primaryPhoneCountryId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      secondaryPhoneNumber: PropTypes.string,
      secondaryPhoneCountryId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      email: PropTypes.string,
      location: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      emergencyContactName: PropTypes.string,
      emergencyContactPhone: PropTypes.string,
    }).isRequired,
    system: PropTypes.shape({
      roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      activeStatus: PropTypes.bool,
      statusReason: PropTypes.string,
      limitedNotifications: PropTypes.bool,
      ownFleet: PropTypes.bool,
      eps: PropTypes.string,
      arl: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onUpdateChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  profileImageId: PropTypes.string,
  isUpdating: PropTypes.bool.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
};

export default UserProfileEdit;